
import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay';

interface ExpenseByQuarterTableProps {
  data: any[]; // Keep for backward compatibility but won't be used
}

interface ExpenseGraphData {
  language: string;
  q4_2024: number;
  q1_2025: number;
  q2_2025: number;
  q3_2025: number;
  q4_2025: number;
  total: number;
}

export const ExpenseByQuarterTable = ({ data }: ExpenseByQuarterTableProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const [expenseData, setExpenseData] = useState<ExpenseGraphData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Define quarters for display
  const quarters = ['Q4 24', 'Q1 25', 'Q2 25', 'Q3 25', 'Q4 25'];

  useEffect(() => {
    const fetchExpenseData = async () => {
      try {
        setIsLoading(true);
        const { data: viewData, error } = await supabase
          .from('expense_graph_view')
          .select('*')
          .order('total', { ascending: false });

        if (error) {
          console.error('Error fetching expense graph data:', error);
          return;
        }

        setExpenseData(viewData || []);
      } catch (error) {
        console.error('Error fetching expense data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExpenseData();
  }, []);

  // Calculate quarter totals for summary cards
  const quarterTotals = quarters.map((quarter, index) => {
    const quarterKey = ['q4_2024', 'q1_2025', 'q2_2025', 'q3_2025', 'q4_2025'][index];
    const total = expenseData.reduce(
      (sum, lang) => sum + (lang[quarterKey as keyof ExpenseGraphData] as number || 0),
      0
    );
    return { quarter, total };
  });

  // Calculate grand total
  const grandTotal = expenseData.reduce((sum, lang) => sum + (lang.total || 0), 0);

  if (isLoading) {
    return (
      <Card className={`${isLightMode ? 'bg-white' : 'bg-[#2A2A2A]'} border ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'} relative min-h-[400px]`}>
        <TableLoadingOverlay isLoading={true} message="Loading expense data..." />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vibrant Color-coded summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {quarterTotals.map((qt, index) => (
          <Card key={qt.quarter} className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium opacity-90 mb-1">
                    {qt.quarter}
                  </div>
                  <div className="text-xl font-bold">
                    ${qt.total.toLocaleString()}
                  </div>
                </div>
                <DollarSign className="h-6 w-6 opacity-80" />
              </div>
            </CardContent>
          </Card>
        ))}
        
        {/* Grand Total Card - Enhanced */}
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium opacity-90 mb-1">
                  GRAND TOTAL
                </div>
                <div className="text-xl font-bold">
                  ${grandTotal.toLocaleString()}
                </div>
              </div>
              <DollarSign className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed table */}
      <Card className={`${isLightMode ? 'bg-white' : 'bg-[#2A2A2A]'} border ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'}`}>
        <CardHeader>
          <CardTitle className={isLightMode ? 'text-gray-900' : 'text-white'}>
            Quarterly Expense Breakdown by Language
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={isLightMode ? "text-gray-800 font-semibold bg-gray-100" : "text-[#8E9196]"}>
                  Language
                </TableHead>
                {quarters.map(quarter => (
                  <TableHead key={quarter} className={isLightMode ? "text-gray-800 font-semibold bg-gray-100" : "text-[#8E9196]"}>
                    {quarter}
                  </TableHead>
                ))}
                <TableHead className={isLightMode ? "text-gray-800 font-semibold bg-gray-100" : "text-[#8E9196]"}>
                  Total
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenseData.map(language => (
                <TableRow key={language.language} className={isLightMode ? "hover:bg-gray-50" : ""}>
                  <TableCell className={`font-medium ${isLightMode ? "text-gray-900" : ""}`}>
                    {language.language}
                  </TableCell>
                  <TableCell className={isLightMode ? "text-gray-800" : ""}>
                    ${(language.q4_2024 || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className={isLightMode ? "text-gray-800" : ""}>
                    ${(language.q1_2025 || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className={isLightMode ? "text-gray-800" : ""}>
                    ${(language.q2_2025 || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className={isLightMode ? "text-gray-800" : ""}>
                    ${(language.q3_2025 || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className={isLightMode ? "text-gray-800" : ""}>
                    ${(language.q4_2025 || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className={`font-bold ${isLightMode ? "text-gray-900" : ""}`}>
                    ${(language.total || 0).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Add a Grand Total row at the bottom */}
              <TableRow className={`border-t-2 ${isLightMode ? "border-gray-300 bg-gray-50" : "border-gray-700"}`}>
                <TableCell className={`font-bold ${isLightMode ? "text-gray-900" : ""}`}>
                  Grand Total
                </TableCell>
                {quarters.map((quarter, index) => {
                  const quarterKey = ['q4_2024', 'q1_2025', 'q2_2025', 'q3_2025', 'q4_2025'][index];
                  const columnTotal = expenseData.reduce(
                    (sum, lang) => sum + (lang[quarterKey as keyof ExpenseGraphData] as number || 0),
                    0
                  );
                  
                  return (
                    <TableCell key={quarter} className={`font-bold ${isLightMode ? "text-gray-800" : ""}`}>
                      ${columnTotal.toLocaleString()}
                    </TableCell>
                  );
                })}
                {/* Grand total */}
                <TableCell className={`font-bold ${isLightMode ? "text-gray-900" : ""}`}>
                  ${grandTotal.toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
